import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, XCircle, FileText, Upload, RefreshCw, AlertTriangle } from 'lucide-react';

interface PriceUpdate {
  identifier: string;
  price: string;
}

interface UpdateResult {
  total: number;
  success: number;
  failed: Array<{
    identifier: string;
    reason: string;
  }>;
  details: Array<{
    identifier: string;
    title: string;
    oldPrice: string;
    newPrice: string;
  }>;
}

export default function ProductConfiguration() {
  console.log('ProductConfiguration component rendering');
  const [identifierType, setIdentifierType] = useState<string>('handle');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [processedData, setProcessedData] = useState<PriceUpdate[]>([]);
  const [updateResult, setUpdateResult] = useState<UpdateResult | null>(null);
  const [activeTab, setActiveTab] = useState<string>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse CSV content into an array of product updates
  const parseCSV = (content: string) => {
    const lines = content.split('\n');
    const result: PriceUpdate[] = [];
    let headerSkipped = false;

    // Process each line
    for (const line of lines) {
      // Skip empty lines
      if (!line.trim()) continue;
      
      // Skip header row
      if (!headerSkipped) {
        headerSkipped = true;
        continue;
      }

      // Parse CSV line, handling both comma and semicolon delimiters
      const delimiter = line.includes(';') ? ';' : ',';
      const parts = line.split(delimiter);
      
      if (parts.length >= 2) {
        const identifier = parts[0].trim();
        const price = parts[1].trim();
        
        if (identifier && price) {
          result.push({ identifier, price });
        }
      }
    }

    return result;
  };

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const parsed = parseCSV(content);
      setProcessedData(parsed);
      setActiveTab('review');
      toast({
        title: "CSV Loaded",
        description: `Found ${parsed.length} price updates to review`,
      });
    };
    reader.onerror = () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to read the CSV file"
      });
    };
    reader.readAsText(file);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (processedData.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No data to update. Please upload a CSV file first."
      });
      return;
    }

    try {
      setIsUploading(true);
      
      const response = await fetch('/api/admin/shopify/update-prices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          priceData: processedData,
          identifierType
        })
      });

      if (response.ok) {
        const result = await response.json();
        setUpdateResult(result.result);
        setActiveTab('results');
        toast({
          title: "Success",
          description: `Updated ${result.result.success} of ${result.result.total} product prices`
        });
      } else {
        const error = await response.json();
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to update prices"
        });
      }
    } catch (error) {
      console.error('Error updating prices:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred"
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Reset the form to start over
  const handleReset = () => {
    setProcessedData([]);
    setUpdateResult(null);
    setActiveTab('upload');
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full py-4">
      <h2 className="text-2xl font-semibold mb-6">Shopify Bulk Price Update</h2>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upload">Upload CSV</TabsTrigger>
          <TabsTrigger value="review" disabled={processedData.length === 0}>Review Updates</TabsTrigger>
          <TabsTrigger value="results" disabled={!updateResult}>Results</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload">
          <Card>
            <CardHeader>
              <CardTitle>Upload Price Data</CardTitle>
              <CardDescription>
                Upload a CSV file with product identifiers and new prices.
                The CSV should have a header row and two columns: identifier and price.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => {
                e.preventDefault();
                if (processedData.length > 0) {
                  setActiveTab('review');
                }
              }}>
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <label htmlFor="identifier-type" className="text-sm font-medium">
                      Identifier Type
                    </label>
                    <select
                      id="identifier-type"
                      value={identifierType}
                      onChange={(e) => setIdentifierType(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="handle">Product Handle</option>
                      <option value="sku">SKU</option>
                      <option value="id">Product ID</option>
                    </select>
                    <p className="text-sm text-muted-foreground">
                      Identifies the product in your CSV file
                    </p>
                  </div>
                  
                  <div className="grid gap-2">
                    <label htmlFor="csv-file" className="text-sm font-medium">
                      CSV File
                    </label>
                    <div className="grid gap-2">
                      <input
                        id="csv-file"
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                      <p className="text-sm text-muted-foreground">
                        File should be in CSV format with header row. Example:
                      </p>
                      <pre className="bg-muted p-2 rounded-md text-xs overflow-x-auto">
                        identifier,price<br/>
                        product-handle,29.99<br/>
                        another-product,19.95
                      </pre>
                    </div>
                  </div>
                  
                  {processedData.length > 0 && (
                    <div className="bg-blue-50 p-4 rounded-md flex items-start space-x-3">
                      <FileText className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div>
                        <h5 className="font-medium text-blue-900">CSV Loaded</h5>
                        <p className="text-blue-800 text-sm mt-1">
                          Found {processedData.length} price updates. 
                          Go to the "Review Updates" tab to confirm.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={handleReset}>Clear</Button>
              <Button 
                onClick={() => processedData.length > 0 && setActiveTab('review')}
                disabled={processedData.length === 0}
              >
                Continue to Review
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="review">
          <Card>
            <CardHeader>
              <CardTitle>Review Price Updates</CardTitle>
              <CardDescription>
                Review the {processedData.length} price updates before applying them to your Shopify store.
                Using identifier type: <strong>{identifierType}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Product Identifier</TableHead>
                      <TableHead>New Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {processedData.slice(0, 100).map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.identifier}</TableCell>
                        <TableCell>${item.price}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {processedData.length > 100 && (
                  <div className="p-2 text-center text-sm text-muted-foreground">
                    Showing first 100 of {processedData.length} updates
                  </div>
                )}
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-md flex items-start space-x-3 mt-4">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
                <div>
                  <h5 className="font-medium text-yellow-900">Important</h5>
                  <p className="text-yellow-800 text-sm mt-1">
                    This action will immediately update prices on your Shopify store.
                    Make sure all prices are correct before proceeding.
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setActiveTab('upload')}>Back</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Update Prices
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="results">
          {updateResult && (
            <Card>
              <CardHeader>
                <CardTitle>Update Results</CardTitle>
                <CardDescription>
                  Completed updating prices for {updateResult.total} products
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold">{updateResult.total}</div>
                          <div className="text-xs text-muted-foreground">Total Products</div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{updateResult.success}</div>
                          <div className="text-xs text-muted-foreground">Successfully Updated</div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-600">{updateResult.failed.length}</div>
                          <div className="text-xs text-muted-foreground">Failed Updates</div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {updateResult.details.length > 0 && (
                    <div>
                      <h3 className="font-medium text-lg mb-2 flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
                        Successful Updates
                      </h3>
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product</TableHead>
                              <TableHead>Old Price</TableHead>
                              <TableHead>New Price</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {updateResult.details.slice(0, 50).map((detail, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <div className="font-medium">{detail.title}</div>
                                  <div className="text-sm text-muted-foreground">{detail.identifier}</div>
                                </TableCell>
                                <TableCell>${detail.oldPrice}</TableCell>
                                <TableCell className="font-medium">${detail.newPrice}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {updateResult.details.length > 50 && (
                          <div className="p-2 text-center text-sm text-muted-foreground">
                            Showing first 50 of {updateResult.details.length} successful updates
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {updateResult.failed.length > 0 && (
                    <div>
                      <h3 className="font-medium text-lg mb-2 flex items-center">
                        <XCircle className="h-5 w-5 mr-2 text-red-500" />
                        Failed Updates
                      </h3>
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product Identifier</TableHead>
                              <TableHead>Error Reason</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {updateResult.failed.map((failed, index) => (
                              <TableRow key={index}>
                                <TableCell>{failed.identifier}</TableCell>
                                <TableCell>{failed.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={handleReset}>
                  Start New Upload
                </Button>
                {updateResult.failed.length > 0 && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      // Filter out successful updates and keep only failed ones
                      const failedIdentifiers = updateResult.failed.map(f => f.identifier);
                      const remainingUpdates = processedData.filter(item => 
                        failedIdentifiers.includes(item.identifier)
                      );
                      setProcessedData(remainingUpdates);
                      setUpdateResult(null);
                      setActiveTab('review');
                      toast({
                        title: "Retrying Failed Updates",
                        description: `Loaded ${remainingUpdates.length} failed updates for retry`
                      });
                    }}
                  >
                    Retry Failed Updates
                  </Button>
                )}
              </CardFooter>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}